import React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { useLogin } from 'lib/core/hooks'
import { Button, Form, Toast } from 'lib/ui/components'
import { Actions } from 'app/livbrite/components/StyledForm'
import { Wrapper, ForgotPassword, Heading } from './components/Styled'
import { schema, validation } from './schema'

interface Props {
  shouldRedirectToAccount?: boolean
  avoidRedirect?: boolean
  setCheckoutState?: (param: string) => void
}

export interface FormValues {
  email: string
  password: string
}

export const LoginForm: React.FC<Props> = ({ shouldRedirectToAccount, avoidRedirect, setCheckoutState }) => {
  const history = useHistory<{ from?: any, detail?: string, AddToWishlist?: boolean } | undefined>()
  const { formatMessage } = useIntl()

  const { login, error, isFetching } = useLogin({
    onSuccess: handleSuccess,
    onFailure: handleFailure
  })

  const from = history.location.state?.from
  const fromPath = typeof from === 'string' ? from : from?.pathname
  const isAddToWishlist = history.location.state?.AddToWishlist

  const handleCreateAccountClick = () => {
    if (avoidRedirect) {
      setCheckoutState?.('create')
    } else {
      history.push('/customer/account/create')
    }
  }

  function handleSuccess () {
    if (avoidRedirect) {
      return
    }

    if (fromPath) {
      history.push({
        pathname: fromPath,
        state: {
          from: history.location.pathname,
          AddToWishlist: isAddToWishlist
        }
      })
    } else {
      if (shouldRedirectToAccount) {
        history.push('/customer/account')
      } else {
        history.push('/')
      }
    }
  }

  function handleFailure () {
    Toast.toastFailure({ messageId: error })
  }

  function handleSubmit (values: FormValues) {
    login({
      ...values,
      persistent: Boolean(process.env.REACT_APP_PERSISTENT_LOGIN)
    })
  }

  return (
    <Wrapper>
      <Heading>
        <h1>
          <FormattedMessage
            id='customer.account.login.title'
          />
        </h1>
        <span>
          Don't have an account?
          <span
            className='action'
            onClick={handleCreateAccountClick}
          >
            Create one now
          </span>
        </span>
      </Heading>
      <Formik
        initialValues={schema}
        validationSchema={validation(formatMessage)}
        onSubmit={handleSubmit}
      >
        <Form.Wrapper>
          <Field
            name='email'
            label={formatMessage({
              id: 'customer.account.login.label.email'
            })}
            component={Form.Input}
            isRequired
            autoFocus
          />

          <Field
            name='password'
            label={formatMessage({
              id: 'customer.account.login.label.password'
            })}
            component={Form.Input}
            type='password'
            isRequired
          />
          <ForgotPassword>
            <Link to='/customer/account/forgotpassword'>
              <FormattedMessage
                id='action.forgotPassword'
              />
            </Link>
          </ForgotPassword>

          <Actions>
            <Button
              fullWidth
              type='submit'
              isLoading={isFetching}
            >
              <FormattedMessage
                id='action.login'
              />
            </Button>
          </Actions>
        </Form.Wrapper>
      </Formik>
    </Wrapper>
  )
}
